import type { StandardStatus } from '@zoocasa/go-search';
import { getComingSoonSelection, getExpEarlyAccessSelection, type BadgeSelection } from './badge-display-policy';
import { useBadges } from './use-badges';
import { BadgeGroup } from './badge-group';

export interface ComingSoonBadgeProps {
  /** The listing's standard status — the badge only renders when this is Coming Soon */
  standardStatus: StandardStatus | null | undefined;
  /** Optional go-live date (Date or ISO string); when omitted/invalid the badge shows no date */
  onMarketDate?: Date | string | null;
  /** The listing's provider ID — drives the eXp Early Access chip alongside Coming Soon */
  providerId?: number | null;
  /** Where to position the badge over its (position:relative) container */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Distance from the anchored edges */
  offset?: string;
}

/**
 * ComingSoonBadge
 *
 * Self-contained "Coming Soon" overlay badge for any surface that has a listing
 * status — general listing cards, map cluster cards, etc. Renders nothing unless
 * the status is Coming Soon.
 *
 * Why a dedicated wrapper (no other badge has one): the address page builds its
 * badges through BadgeDisplayPolicy, which reads the rich `Listing` class
 * (addedAt, openHouses, virtualTourUrl…). Listing/map cards receive different,
 * leaner shapes (ListingCardData, MapPageListing) that don't fit the policy, so
 * they can't reuse that path. Rather than repeat the
 * getComingSoonSelection → useBadges → BadgeGroup wiring in every card, this
 * wrapper encapsulates it once. The status gating + date logic still live in a
 * single place (getComingSoonSelection), shared with the policy. If more
 * card-only badges appear later, consider generalizing this into a single
 * card-badges component instead of adding more per-badge wrappers.
 *
 * The parent container must be `position: relative` for the overlay to anchor.
 *
 * @example
 * ```tsx
 * <div style={{ position: 'relative' }}>
 *   <ListingImage ... />
 *   <ComingSoonBadge standardStatus={listing.standardStatus} />
 * </div>
 * ```
 */
export function ComingSoonBadge({ standardStatus, onMarketDate, providerId, position = 'top-left', offset = '12px' }: ComingSoonBadgeProps) {
  const comingSoon = getComingSoonSelection(standardStatus, onMarketDate);
  const expEarlyAccess = getExpEarlyAccessSelection(providerId, standardStatus);
  const selections = [comingSoon, expEarlyAccess].filter((selection): selection is BadgeSelection => selection !== null);
  const { badges } = useBadges({ selections });

  if (badges.length === 0) {
    return null;
  }

  return <BadgeGroup badges={badges} position={position} offset={offset} />;
}
